import { useState, useEffect } from "react";
import axios from "axios";
import "./FoodFeedback.css";
import {
  base_url,
  webServiceUrl,
} from "../../../../components/apiCall/webserviceurl";

const apiClient = axios.create({ baseURL: base_url });

// Maps each section id to its submit (POST) and fetch (GET) endpoint.
const SECTION_ENDPOINTS = {
  food: {
    submit: webServiceUrl.food_feedback,
    fetch: webServiceUrl.get_food_feedback,
  },
  room: {
    submit: webServiceUrl.room_feedback,
    fetch: webServiceUrl.get_room_feedback,
  },
};

// ratings object { cleanliness: 4, comfort: 5 } -> [{question_code, rating}]
function ratingsObjectToArray(ratingsObj) {
  return Object.entries(ratingsObj).map(([question_code, rating]) => ({
    question_code,
    rating,
  }));
}

// [{question_code, rating}] -> ratings object { cleanliness: 4, comfort: 5 }
function ratingsArrayToObject(ratingsArr, ratingFields) {
  const obj = Object.fromEntries(ratingFields.map((f) => [f.key, 0]));
  (ratingsArr || []).forEach(({ question_code, rating }) => {
    obj[question_code] = rating;
  });
  return obj;
}

const DEFAULT_SECTIONS_META = {
  food: { label: "Food", icon: UtensilsIcon, accent: "green" },
  room: { label: "Room", icon: BedIcon, accent: "teal" },
};

function RatingCard({
  id,
  label,
  required,
  value,
  onChange,
  error,
  min = 1,
  max = 5,
  minLabel = "Poor",
  maxLabel = "Excellent",
  readOnly = false,
}) {
  const [hoverValue, setHoverValue] = useState(0);

  const values = [];
  for (let v = min; v <= max; v++) values.push(v);

  const numericValue = Number(value) || 0;
  const activeValue = readOnly ? numericValue : hoverValue || numericValue;

  return (
    <div
      className={`rc-field${error ? " rc-field--error" : ""}${
        readOnly ? " rc-field--readonly" : ""
      }`}
      id={id}
    >
      <p className="rc-label">
        {label}
        {required && !readOnly && <span className="rc-required">&nbsp;*</span>}
      </p>

      <div className="rc-stars-row">
        <div
          className="rc-stars"
          role="radiogroup"
          aria-label={label}
          aria-readonly={readOnly}
          onMouseLeave={() => !readOnly && setHoverValue(0)}
        >
          {values.map((v) => {
            const selected = String(value) === String(v);
            const filled = v <= activeValue;
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${v} star${v === 1 ? "" : "s"}`}
                className={`rc-star-btn${filled ? " rc-star-btn--filled" : ""}`}
                disabled={readOnly}
                onMouseEnter={() => !readOnly && setHoverValue(v)}
                onFocus={() => !readOnly && setHoverValue(v)}
                onBlur={() => !readOnly && setHoverValue(0)}
                onClick={() => !readOnly && onChange(v)}
              >
                <svg viewBox="0 0 24 24" className="rc-star-icon">
                  <path d="M12 2.6l2.95 6.28 6.75.77-5.02 4.72 1.36 6.85L12 17.9l-6.04 3.32 1.36-6.85-5.02-4.72 6.75-.77L12 2.6z" />
                </svg>
              </button>
            );
          })}
        </div>
        <span
          className={`rc-star-value${numericValue ? " rc-star-value--set" : ""}`}
        >
          {numericValue ? `${numericValue}/${max}` : "Not rated"}
        </span>
      </div>

      <div className="rc-scale-labels">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>

      {error && <p className="rc-error">{error}</p>}
    </div>
  );
}

/**
 * Props:
 *  - sections: { food: { config, ... }, room: { config, ... } }
 *  - course_get: { class, section, level, combination_code }
 *  - lessonPlanIdentity: object spread into the getLessonPlanDays request body
 *    (was LESSON_PLAN_IDENTITY in your snippet)
 */
export default function FeedbackForm({
  sections,
  course_get,
  lessonPlanIdentity = {},
}) {
  const sectionIds = Object.keys(sections);
  const [activeSection, setActiveSection] = useState(sectionIds[0]);
  const [daysLoading, setDaysLoading] = useState(true);
  const token = localStorage.getItem("token");

  // Independent state per section, keyed by section id.
  // Starts with no days; filled once getLessonPlanDays returns.
  const [sectionState, setSectionState] = useState(() => {
    const initial = {};
    for (const id of sectionIds) {
      initial[id] = makeInitialState(sections[id].config, []);
    }
    return initial;
  });

  // Thank-you popup shown right after a successful submit.
  const [thankYou, setThankYou] = useState({
    open: false,
    dayNumber: null,
    sectionLabel: "",
  });

  // ── Get the days (replaces the hardcoded 5) ────────────────────
  const getLessonPlanDays = async () => {
    try {
      setDaysLoading(true);
      const res = await axios.post(
        webServiceUrl.getLessonPlanDays,
        {
          ...lessonPlanIdentity,
          class_name: course_get?.class,
          section: course_get?.section,
          level: course_get?.level,
          combination_code: course_get?.combination_code,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Real shape: { success: true, data: [{ day: "2025-08-28" }, ...] }
      const rawDays = res?.data?.data || [];
      const mappedDays = Array.isArray(rawDays)
        ? rawDays.map((d) => ({ date: d?.day || "" }))
        : [];

      // Build every section's day list from the fetched days.
      const next = {};
      for (const id of sectionIds) {
        next[id] = makeInitialState(sections[id].config, mappedDays);
      }
      setSectionState(next);

      // Only now fetch already-submitted feedback and merge it in.
      if (mappedDays.length) fetchExistingFeedback();
    } catch (e) {
      console.log(e, "ERROR coming from getLessonPlanDays");
    } finally {
      setDaysLoading(false);
    }
  };

  // ── Fetch existing feedback for each section ───────────────────
  const fetchExistingFeedback = () => {
    sectionIds.forEach((id) => {
      const endpoint = SECTION_ENDPOINTS[id]?.fetch;
      if (!endpoint) return;
      const config = sections[id].config;

      apiClient
        .get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          const records = res?.data?.data ?? [];
          if (!records.length) return;

          setSectionState((prev) => {
            const prevSection = prev[id];
            const nextDays = prevSection.days.map((d, i) => {
              const dayNumber = i + 1;
              const record = records.find((r) => r.day_number === dayNumber);
              if (!record) return d;
              return {
                ...d,
                mealType: record.mealType ?? d.mealType,
                ratings: ratingsArrayToObject(
                  record.ratings,
                  config.ratingFields,
                ),
                recommend:
                  typeof record.recommend === "boolean"
                    ? record.recommend
                    : d.recommend,
                comments: record.comments ?? d.comments,
                submitted: true,
              };
            });
            return { ...prev, [id]: { ...prevSection, days: nextDays } };
          });
        })
        .catch((err) => {
          console.error(`Failed to fetch ${id} feedback:`, err);
        });
    });
  };

  useEffect(() => {
    getLessonPlanDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guards: nothing below works until days exist.
  // (All hooks are above, so hook order stays stable.)
  if (daysLoading) return <div className="ff-page">Loading days...</div>;
  if (!sectionState[activeSection]?.days.length)
    return <div className="ff-page">No days available yet.</div>;

  const activeConfig = sections[activeSection].config;
  const activeMeta = {
    ...DEFAULT_SECTIONS_META[activeSection],
    ...sections[activeSection],
  };
  const state = sectionState[activeSection];

  const patchState = (patch) => {
    setSectionState((prev) => ({
      ...prev,
      [activeSection]: { ...prev[activeSection], ...patch },
    }));
  };

  const dayCount = state.days.length; // was: activeConfig.dayCount ?? 5
  const dayIndex = state.activeDay - 1;
  const current = state.days[dayIndex];
  const completedCount = state.days.filter((d) => d.submitted).length;
  const allDone = completedCount === dayCount;
  const progressPct = Math.round((completedCount / dayCount) * 100);
  const isReadOnly = current.submitted;

  const updateCurrentDay = (patch) => {
    if (isReadOnly) return;
    patchState({
      days: state.days.map((d, i) => (i === dayIndex ? { ...d, ...patch } : d)),
    });
  };

  const setMealType = (type) => updateCurrentDay({ mealType: type });
  const setRating = (key, value) =>
    updateCurrentDay({ ratings: { ...current.ratings, [key]: value } });
  const setRecommend = (value) => updateCurrentDay({ recommend: value });
  const setComments = (value) => updateCurrentDay({ comments: value });

  const goToDay = (dayNumber) => {
    patchState({ activeDay: dayNumber, error: "" });
  };

  const switchSection = (id) => {
    if (id === activeSection) return;
    setActiveSection(id);
  };

  const closeThankYou = () => setThankYou((t) => ({ ...t, open: false }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Feedback is one-time only — guard against re-submission.
    if (current.submitted) return;

    const missing = activeConfig.ratingFields.filter(
      (f) => current.ratings[f.key] === 0,
    );
    if (missing.length > 0) {
      patchState({ error: "Please rate every category before submitting." });
      return;
    }

    const payload = {
      day_number: state.activeDay,
      date: current.date, // "YYYY-MM-DD" from getLessonPlanDays
      ...(activeConfig.mealTypes ? { mealType: current.mealType } : {}),
      ratings: ratingsObjectToArray(current.ratings),
      recommend: current.recommend,
      comments: current.comments,
      submittedAt: new Date().toISOString(),
    };

    const endpoint = SECTION_ENDPOINTS[activeSection]?.submit;
    const submittedDayNumber = state.activeDay;

    try {
      patchState({ isSubmitting: true, error: "" });

      if (endpoint) {
        await apiClient.post(endpoint, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (activeConfig.onSubmitDay) {
        activeConfig.onSubmitDay(payload);
      } else {
        console.log(
          `${activeConfig.title} submitted for Day ${submittedDayNumber}:`,
          payload,
        );
      }

      const nextDays = state.days.map((d, i) =>
        i === dayIndex ? { ...d, submitted: true } : d,
      );
      const nextIncomplete = nextDays.findIndex((d) => !d.submitted);

      patchState({
        days: nextDays,
        error: "",
        isSubmitting: false,
        activeDay: nextIncomplete !== -1 ? nextIncomplete + 1 : state.activeDay,
      });

      // Pop the thank-you modal for the day just submitted.
      setThankYou({
        open: true,
        dayNumber: submittedDayNumber,
        sectionLabel: activeMeta.label,
      });
    } catch (err) {
      console.error(`Failed to submit ${activeSection} feedback:`, err);
      patchState({
        isSubmitting: false,
        error:
          "Something went wrong while saving your feedback. Please try again.",
      });
    }
  };

  return (
    <div className="ff-page">
      {/* Section switcher */}
      <div
        className="ff-switcher"
        role="tablist"
        aria-label="Feedback category"
      >
        {sectionIds.map((id) => {
          const meta = { ...DEFAULT_SECTIONS_META[id], ...sections[id] };
          const Icon = meta.icon;
          const isActive = id === activeSection;
          const sectionCompleted = sectionState[id].days.filter(
            (d) => d.submitted,
          ).length;
          const sectionTotal = sectionState[id].days.length; // was: config.dayCount ?? 5
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`ff-switch-tab ff-accent-${meta.accent}${isActive ? " ff-switch-tab-active" : ""}`}
              onClick={() => switchSection(id)}
            >
              <span className="ff-switch-icon">
                <Icon />
              </span>
              <span className="ff-switch-text">
                <span className="ff-switch-label">{meta.label}</span>
                <span className="ff-switch-sub">
                  {sectionCompleted}/{sectionTotal} days
                </span>
              </span>
              {sectionTotal > 0 && sectionCompleted === sectionTotal && (
                <span className="ff-switch-done">
                  <CheckCircleIcon />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div
        className={`ff-panel ff-accent-${activeMeta.accent}`}
        key={activeSection}
      >
        <div className="ff-panel-head">
          <span className="ff-panel-icon">
            <activeMeta.icon />
          </span>
          <div>
            <h2 className="ff-panel-title">{activeConfig.title}</h2>
            <p className="ff-panel-subtitle">{activeConfig.subtitle}</p>
          </div>
        </div>

        {/* Day progress */}
        <div className="ff-progress-row">
          <span className="ff-progress-text">
            <strong>{completedCount}</strong> of {dayCount} days completed
          </span>
          <span className="ff-progress-pct">{progressPct}%</span>
        </div>
        <div className="ff-progress-track">
          <div
            className="ff-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Day tabs */}
        <div className="ff-day-tab-row">
          {state.days.map((d, i) => {
            const dayNumber = i + 1;
            return (
              <button
                type="button"
                key={dayNumber}
                className={`ff-day-tab${
                  state.activeDay === dayNumber ? " ff-day-tab-active" : ""
                }${d.submitted ? " ff-day-tab-done" : ""}`}
                onClick={() => goToDay(dayNumber)}
              >
                {d.submitted && <span className="ff-day-tab-marker">✓</span>}
                Day {dayNumber}
                {/* {d.date && <span className="ff-day-tab-date"> · {d.date}</span>} */}
              </button>
            );
          })}
        </div>

        {/* {isReadOnly && (
          <div className="ff-readonly-banner">
            <span className="ff-readonly-icon">
              <CheckCircleIcon />
            </span>
            Feedback for Day {state.activeDay} has been submitted and can't be
            edited.
          </div>
        )} */}

        <form className="ff-form" onSubmit={handleSubmit}>
          {activeConfig.mealTypes && (
            <div className="ff-field">
              <label className="ff-label">
                Meal type
                <span className="ff-label-day"> · Day {state.activeDay}</span>
              </label>
              <div className="ff-segmented">
                {activeConfig.mealTypes.map((type) => (
                  <button
                    type="button"
                    key={type}
                    className={`ff-segment${
                      current.mealType === type ? " ff-segment-active" : ""
                    }`}
                    disabled={isReadOnly}
                    onClick={() => setMealType(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="ff-rating-grid">
            {activeConfig.ratingFields.map((field) => (
              <RatingCard
                key={field.key}
                id={`${field.key}-${activeSection}-day${state.activeDay}`}
                label={field.label}
                required
                value={current.ratings[field.key]}
                onChange={(v) => setRating(field.key, v)}
                error={Boolean(state.error) && current.ratings[field.key] === 0}
                readOnly={isReadOnly}
              />
            ))}
          </div>

          <div className="ff-field">
            <label className="ff-label">{activeConfig.recommendQuestion}</label>
            <div className="ff-yesno">
              <button
                type="button"
                className={`ff-yesno-btn${
                  current.recommend === true ? " ff-yesno-active" : ""
                }`}
                disabled={isReadOnly}
                onClick={() => setRecommend(true)}
              >
                <ThumbUpIcon />
                Yes
              </button>
              <button
                type="button"
                className={`ff-yesno-btn${
                  current.recommend === false ? " ff-yesno-active-negative" : ""
                }`}
                disabled={isReadOnly}
                onClick={() => setRecommend(false)}
              >
                <ThumbDownIcon />
                No
              </button>
            </div>
          </div>

          <div className="ff-field">
            <label className="ff-label" htmlFor={`comments-${activeSection}`}>
              Additional comments (optional)
            </label>
            <textarea
              id={`comments-${activeSection}`}
              className="ff-textarea"
              placeholder="Anything you'd like to add..."
              rows={4}
              value={current.comments}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          {state.error && <p className="ff-error">{state.error}</p>}

          {!isReadOnly && (
            <div className="ff-submit-row">
              <button
                type="submit"
                className="ff-submit-btn"
                disabled={state.isSubmitting}
              >
                {state.isSubmitting
                  ? "Saving..."
                  : `Submit Day ${state.activeDay} feedback`}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* <ThankYouModal
        open={thankYou.open}
        dayNumber={thankYou.dayNumber}
        sectionLabel={thankYou.sectionLabel}
        onClose={closeThankYou}
      /> */}
    </div>
  );
}

// ── State helpers ──────────────────────────────────────────────

function makeEmptyDay(config, date = "") {
  return {
    date, // "YYYY-MM-DD" from the API
    mealType: config.mealTypes
      ? (config.mealTypes[1] ?? config.mealTypes[0])
      : undefined,
    ratings: Object.fromEntries(config.ratingFields.map((f) => [f.key, 0])),
    recommend: null,
    comments: "",
    submitted: false,
  };
}

// days = [{ date }, ...] from the API; empty until they load
function makeInitialState(config, days = []) {
  return {
    days: days.map((d) => makeEmptyDay(config, d.date)),
    activeDay: 1,
    error: "",
    isSubmitting: false,
  };
}

// ── Thank-you popup ────────────────────────────────────────────

function ThankYouModal({ open, onClose, dayNumber, sectionLabel }) {
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="ff-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Feedback submitted"
      onClick={onClose}
    >
      <div className="ff-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="ff-modal-close-x"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <span className="ff-modal-icon">
          <CheckCircleIcon />
        </span>
        <h3 className="ff-modal-title">Thank you!</h3>
        <p className="ff-modal-text">
          Your Day {dayNumber} {sectionLabel?.toLowerCase()} feedback has been
          submitted successfully.
        </p>
        <button type="button" className="ff-modal-close-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

// ── Icons ───────────────────────────────────────────────────────

function ThumbUpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <path
        d="M7 8.5V17H4.5A1.5 1.5 0 0 1 3 15.5v-5A1.5 1.5 0 0 1 4.5 9H7Zm0 0 3-6a2 2 0 0 1 2 2v3h3.5a1.5 1.5 0 0 1 1.45 1.87l-1.2 5A1.5 1.5 0 0 1 14.3 17H7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ThumbDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <path
        d="M13 11.5V3H15.5A1.5 1.5 0 0 1 17 4.5v5a1.5 1.5 0 0 1-1.5 1.5H13Zm0 0-3 6a2 2 0 0 1-2-2v-3H4.5a1.5 1.5 0 0 1-1.45-1.87l1.2-5A1.5 1.5 0 0 1 5.7 4H13"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M6.5 10.3l2.2 2.2 4.8-5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UtensilsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <path
        d="M5 2v6.5M3.5 2v4.5a1.5 1.5 0 0 0 3 0V2M5 8.5V18M14.5 2c-1.4 0-2.5 1.9-2.5 5.5 0 2.4 1 3.5 2 4v6.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BedIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <path
        d="M2.5 16V6a1 1 0 0 1 1-1H10a1 1 0 0 1 1 1v3.5M2.5 12.5h15V16M17.5 12.5v-3a2 2 0 0 0-2-2h-4v3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="5.25"
        cy="7.75"
        r="1.15"
        stroke="currentColor"
        strokeWidth="1.3"
      />
    </svg>
  );
}